import React, { useCallback, useContext, useMemo } from 'react';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';
import { Field, Form } from 'react-final-form';
import { Button, Loader } from 'ncoded-component-library';
import ColorPickerField from 'components/ColorPickerField';
import InputField from 'components/InputField';
import validations from 'validations';
import { DropzoneDelete, DropzoneUpload } from 'models/Dropzone';
import { Enterprise, UpdateEnterpriseValue } from 'models/Enterprises';
import PopNotificationsContext from 'providers/PopNotifications/PopNotifications.context';
import api from 'api';
import Dropzone from 'components/Dropzone';
import { RESOLVE_COLORS } from 'hooks/useBrandingColors';

import './BrandingForm.styles.scss';
import './BrandingForm.styles.responsive.scss';

const DESKTOP_LOGO_NAME = 'logo';
const MOBILE_LOGO_NAME = 'mobileLogo';

type BrandingFormProps = {
  className?: string;
  enterprise: Enterprise;
  updateEnterprise: (enterprise: Partial<Enterprise>) => void;
};

const BrandingForm: React.FC<BrandingFormProps> = (props) => {
  const {
    className,
    enterprise: {
      _id,
      brandName,
      logo,
      mobileLogo,
      primaryColor = RESOLVE_COLORS.primaryColor,
      secondaryColor = RESOLVE_COLORS.secondaryColor,
    },
    updateEnterprise,
  } = props;

  const { popSuccess, popServerError } = useContext(PopNotificationsContext);

  const classes = classNames('branding-form', className);

  const { t } = useTranslation();

  const initialValues = useMemo(
    () => ({
      brandName,
      primaryColor,
      secondaryColor,
    }),
    [brandName, primaryColor, secondaryColor],
  );

  const uploadLogo: DropzoneUpload = useCallback(
    async (file, fileName, config) => {
      const fileUploaded = await api.enterprises.uploadLogo(
        file,
        fileName,
        _id,
        config,
      );

      updateEnterprise({ [fileName]: fileUploaded });
      return fileUploaded;
    },
    [_id, updateEnterprise],
  );

  const deleteLogo: DropzoneDelete = useCallback(
    async (file, fileName) => {
      const {
        data: { fileId },
      } = await api.enterprises.deleteLogo(fileName, file._id, _id);
      updateEnterprise({ [fileName]: null });
      return fileId;
    },
    [_id, updateEnterprise],
  );

  const onSubmit = useCallback(
    async (values: UpdateEnterpriseValue) => {
      try {
        const { data } = await api.enterprises.updateEnterprise(_id, values);
        updateEnterprise(data);
        popSuccess(t('brandingUpdated'));
      } catch (e) {
        popServerError(e);
      }
    },
    [_id, popServerError, popSuccess, t, updateEnterprise],
  );

  return (
    <Form
      onSubmit={onSubmit}
      initialValues={initialValues}
      render={({
        submitting,
        invalid,
        dirty,
        handleSubmit,
        form: { reset },
      }) => (
        <form onSubmit={handleSubmit} className={classes}>
          {submitting && <Loader />}

          <div className="fields">
            <section className="left">
              <Field
                name="brandName"
                component={InputField}
                label={t('enterpriseName')}
                required
                validate={validations.required()}
              />
              <Field
                name="primaryColor"
                component={ColorPickerField}
                label={t('primaryColour')}
                required
                validate={validations.required()}
              />
              <Field
                name="secondaryColor"
                component={ColorPickerField}
                label={t('secondaryColour')}
                required
                validate={validations.required()}
              />
              <p className="description">
                {t('brandingColorsWillBeReflectedIn')}
              </p>
            </section>

            <section className="right">
              <div>
                <label className="required">{t('desktopLogo')}</label>
                <Dropzone
                  fileName={DESKTOP_LOGO_NAME}
                  files={logo ? [logo] : []}
                  required
                  accept="image/*"
                  replaceFile
                  maxFiles={1}
                  uploadRequest={uploadLogo}
                  deleteRequest={deleteLogo}
                />
              </div>

              <div>
                <label>{t('mobileLogo')}</label>
                <Dropzone
                  className="mobile-logo"
                  fileName={MOBILE_LOGO_NAME}
                  files={mobileLogo ? [mobileLogo] : []}
                  accept="image/*"
                  maxFiles={1}
                  uploadRequest={uploadLogo}
                  deleteRequest={deleteLogo}
                />
              </div>
            </section>
          </div>

          <div className="actions">
            {dirty && (
              <Button variant="link" onClick={() => reset()}>
                {t('cancel')}
              </Button>
            )}

            <Button
              type="submit"
              disabled={!dirty || submitting || invalid || !logo}
            >
              {t('submit')}
            </Button>
          </div>
        </form>
      )}
    />
  );
};

export default BrandingForm;
